/**
 * Where the console gets its identity configuration.
 *
 * All three values are public by nature — they appear in the browser's address
 * bar during sign-in — so baking them into the bundle costs nothing. The client
 * is a PUBLIC client with no secret: there is nowhere in a single-page app to
 * keep one, and a "secret" shipped to every browser is just a longer client id.
 * PKCE is what replaces it (ADR-015).
 */
export interface AuthConfig {
  /** e.g. https://verydia-control-prod-000.auth.us-east-1.amazoncognito.com */
  readonly domain: string;
  readonly clientId: string;
  /** Must match a callback URL registered on the user pool client, exactly. */
  readonly redirectUri: string;
  readonly logoutUri: string;
}

function required(name: string, value: string | undefined): string {
  if (value === undefined || value.length === 0) {
    // Failing loudly at startup beats a sign-in button that redirects to
    // "undefined" and produces an unreadable Cognito error.
    throw new Error(`${name} is not set. The console cannot authenticate without it.`);
  }
  return value.replace(/\/$/, '');
}

export function loadAuthConfig(env: Record<string, string | undefined>): AuthConfig {
  const origin = (env['VITE_VC_CONSOLE_ORIGIN'] ?? window.location.origin).replace(/\/$/, '');
  return {
    domain: required('VITE_VC_COGNITO_DOMAIN', env['VITE_VC_COGNITO_DOMAIN']),
    clientId: required('VITE_VC_COGNITO_CLIENT_ID', env['VITE_VC_COGNITO_CLIENT_ID']),
    // Derived from the origin rather than configured separately: these must match
    // the user pool's registered URLs exactly, and two places to edit is one
    // place to get wrong.
    redirectUri: `${origin}/auth/callback`,
    logoutUri: `${origin}/`,
  };
}

export function isAuthConfigured(env: Record<string, string | undefined>): boolean {
  return Boolean(env['VITE_VC_COGNITO_DOMAIN']) && Boolean(env['VITE_VC_COGNITO_CLIENT_ID']);
}
